// ✅ SupervisorPaymentListing.jsx (UPDATED WITH localStorage helper + commonHeaders)
// ✅ Includes:
// - safe localStorage getter
// - support for branchId/branchid, branchType/branchtype, userId/loginuserid/loginUserId
// - commonHeaders via useMemo
// - headers passed in axios calls

import React, { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";
import HeaderBar from "../../components/HeaderBar";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaPlus,
  FaSearch,
  FaChevronUp,
  FaChevronDown,
  FaTrash,
  FaEdit,
} from "react-icons/fa";
import PaymentLayoutWrapper from "../../components/PaymentLayoutWrapper";
import { useNavigate } from "react-router-dom";

const BASE_URL = "https://nissi-india.com";

const SupervisorPaymentListing = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const navigate = useNavigate();
  const [showFilters, setShowFilters] = useState(true);

  // ✅ safe localStorage helper
  const getLS = (key, fallback = "") => {
    if (typeof window === "undefined") return fallback;
    const val = localStorage.getItem(key);
    return val !== null && val !== undefined ? val : fallback;
  };

  // ✅ localStorage values
  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchId = getLS("branchId") || getLS("branchid");
  const branch = getLS("branchType") || getLS("branchtype") || "HO";
  const userId =
    getLS("userId") || getLS("loginuserid") || getLS("loginUserId");

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId]
  );

  const [branches, setBranches] = useState([]);
  const [nameDDL, setNameDDL] = useState([]);
  const [codeDDL, setCodeDDL] = useState([]);
  const [entryDDL, setEntryDDL] = useState([]);

  const [searchData, setSearchData] = useState({
    fromDate: "",
    toDate: "",
    supervisorid: "",
    branchid: branchId || "",
    entryno: "",
  });

  const [paymentData, setPaymentData] = useState([]);
  const [loading, setLoading] = useState(false);

  // ✅ selected payment ids
  const [selectedIds, setSelectedIds] = useState([]);

  const selectedCount = selectedIds.length;

  const allVisibleIds = useMemo(
    () => (paymentData || []).map((x) => x.paymentId).filter(Boolean),
    [paymentData]
  );

  const isAllSelected = useMemo(() => {
    if (allVisibleIds.length === 0) return false;
    return allVisibleIds.every((id) => selectedIds.includes(id));
  }, [allVisibleIds, selectedIds]);

  const handleSearchChange = (field, value) => {
    setSearchData((prev) => ({ ...prev, [field]: value }));
  };

  // ---------------------------
  // API Calls
  // ---------------------------
  const loadBranches = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/supervisorPayment/branches`, {
        headers: commonHeaders,
      });
      setBranches(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to load branches");
      setBranches([]);
    }
  };

  const loadNameDDL = async (bid) => {
    try {
      const res = await axios.get(`${BASE_URL}/api/supervisorPayment/nameDDL`, {
        params: bid ? { branchid: bid } : {},
        headers: commonHeaders,
      });
      setNameDDL(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to load supervisor names");
      setNameDDL([]);
    }
  };

  const loadCodeDDL = async (bid) => {
    try {
      const res = await axios.get(`${BASE_URL}/api/supervisorPayment/codeDDL`, {
        params: bid ? { branchid: bid } : {},
        headers: commonHeaders,
      });
      setCodeDDL(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to load supervisor codes");
      setCodeDDL([]);
    }
  };

  const loadEntryDDL = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/supervisorPayment/entryNoDDL`, {
        headers: commonHeaders,
      });
      setEntryDDL(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to load entry numbers");
      setEntryDDL([]);
    }
  };

  const loadList = async () => {
    try {
      setLoading(true);

      if (!searchData.branchid) {
        toast.warn("Please select Branch");
        setPaymentData([]);
        return;
      }

      const res = await axios.get(`${BASE_URL}/api/supervisorPayment/list`, {
        params: {
          branchid: searchData.branchid,
          supervisorid: searchData.supervisorid || "",
          fromDate: searchData.fromDate || "",
          toDate: searchData.toDate || "",
          entryno: searchData.entryno || "",
        },
        headers: commonHeaders,
      });

      if (res.data?.success === false) {
        toast.error(res.data?.message || "Error loading data");
        setPaymentData([]);
      } else {
        setPaymentData(Array.isArray(res.data?.data) ? res.data.data : []);
      }

      // ✅ reset selection on new search
      setSelectedIds([]);
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message);
      setPaymentData([]);
      setSelectedIds([]);
    } finally {
      setLoading(false);
    }
  };

  // ---------------------------
  // Selection Handlers
  // ---------------------------
  const toggleRow = (paymentId) => {
    setSelectedIds((prev) => {
      if (prev.includes(paymentId)) return prev.filter((x) => x !== paymentId);
      return [...prev, paymentId];
    });
  };

  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      if (isAllSelected) return [];
      return allVisibleIds;
    });
  };

  // ---------------------------
  // Delete Selected
  // ---------------------------
  const handleDeleteSelected = async () => {
    if (selectedIds.length === 0) {
      toast.warn("Please select at least one row to delete.");
      return;
    }

    const ok = window.confirm(
      `Are you sure you want to delete ${selectedIds.length} record(s)?`
    );
    if (!ok) return;

    try {
      setLoading(true);

      await Promise.all(
        selectedIds.map((paymentid) =>
          axios.delete(`${BASE_URL}/api/supervisorPayment/delete`, {
            params: { paymentid },
            headers: commonHeaders,
          })
        )
      );

      toast.success("Deleted successfully");
      await loadList();
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  // ---------------------------
  // Update Selected
  // ---------------------------
  const handleEditSelected = () => {
    if (selectedIds.length === 0) {
      toast.warn("Please select one row to edit.");
      return;
    }
    if (selectedIds.length > 1) {
      toast.warn("Please select only one row to edit.");
      return;
    }

    navigate("/PaymentVouchers/SupervisorPaymentEntry", {
      state: { paymentid: selectedIds[0], editflag: true },
    });
  };

  // ---------------------------
  // Effects
  // ---------------------------
  useEffect(() => {
    loadBranches();
    loadEntryDDL();
    loadNameDDL(branchId || "");
    loadCodeDDL(branchId || "");

    if (branchId) {
      loadList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (searchData.branchid) {
      loadNameDDL(searchData.branchid);
      loadCodeDDL(searchData.branchid);
      setSearchData((prev) => ({ ...prev, supervisorid: "" }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchData.branchid]);

  const handleAddNew = () => navigate("/PaymentVouchers/SupervisorPaymentEntry");
  const handleSearch = () => loadList();

  return (
    <PaymentLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">SUPERVISOR PAYMENT LISTING</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3 mb-6">
            <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
              Supervisor Payment Listing
            </h1>

            <div className="flex flex-wrap items-center gap-3 justify-end">
              <button
                onClick={handleAddNew}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all ${buttonClass} ${hoverClass} text-white`}
              >
                <FaPlus /> Add New
              </button>

              <button
                onClick={handleEditSelected}
                className="flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all bg-indigo-600 hover:bg-indigo-700 text-white"
              >
                <FaEdit /> Edit Selected
              </button>

              <button
                onClick={handleDeleteSelected}
                className="flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all bg-red-600 hover:bg-red-700 text-white"
              >
                <FaTrash /> Delete Selected {selectedCount ? `(${selectedCount})` : ""}
              </button>

              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transition duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>

          {showFilters && (
            <div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    From Date
                  </label>
                  <input
                    type="date"
                    value={searchData.fromDate}
                    onChange={(e) => handleSearchChange("fromDate", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    To Date
                  </label>
                  <input
                    type="date"
                    value={searchData.toDate}
                    onChange={(e) => handleSearchChange("toDate", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Branch Name
                  </label>
                  <select
                    value={searchData.branchid}
                    disabled={branch !== "HO"}
                    onChange={(e) => handleSearchChange("branchid", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  >
                    <option value="">Select</option>
                    {branches.map((b) => (
                      <option key={b.fld_branchid} value={b.fld_branchid}>
                        {b.fld_branchname}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Supervisor Name
                  </label>
                  <select
                    value={searchData.supervisorid}
                    onChange={(e) => handleSearchChange("supervisorid", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  >
                    <option value="">Select</option>
                    {nameDDL.map((x) => (
                      <option key={x.fld_supervisorid} value={x.fld_supervisorid}>
                        {x.fld_supervisorname}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Supervisor Code
                  </label>
                  <select
                    value={searchData.supervisorid}
                    onChange={(e) => handleSearchChange("supervisorid", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  >
                    <option value="">Select</option>
                    {codeDDL.map((x) => (
                      <option key={x.fld_supervisorid} value={x.fld_supervisorid}>
                        {x.fld_supervisorcode}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Entry Number
                  </label>
                  <select
                    value={searchData.entryno}
                    onChange={(e) => handleSearchChange("entryno", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  >
                    <option value="">Select</option>
                    {entryDDL.map((x) => (
                      <option key={x.fld_paymentid} value={x.fld_entryno}>
                        {x.fld_entryno}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex justify-center items-center gap-6 mb-6">
                <button
                  onClick={handleSearch}
                  disabled={loading}
                  className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all ${buttonClass} ${hoverClass} text-white`}
                >
                  <FaSearch /> {loading ? "Searching..." : "Search"}
                </button>
              </div>
            </div>
          )}

          <div className={`rounded-xl shadow-sm border overflow-hidden ${isDark ? "border-gray-700" : "border-gray-200"}`}>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className={`${buttonClass} text-white`}>
                  <tr>
                    <th className="px-3 py-3 text-center font-semibold border-r border-white/20">
                      <input
                        type="checkbox"
                        checked={isAllSelected}
                        onChange={toggleSelectAll}
                        className="h-4 w-4 cursor-pointer"
                        title="Select All"
                      />
                    </th>
                    <th className="px-4 py-3 text-center font-semibold border-r border-white/20">Sl.No</th>
                    <th className="px-4 py-3 text-center font-semibold border-r border-white/20">Date</th>
                    <th className="px-4 py-3 text-center font-semibold border-r border-white/20">Entry Number</th>
                    <th className="px-4 py-3 text-center font-semibold border-r border-white/20">Supervisor Name</th>
                    <th className="px-4 py-3 text-center font-semibold">Branch Name</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {paymentData.length === 0 ? (
                    <tr>
                      <td colSpan={6} className={`py-6 text-center ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                        No records found
                      </td>
                    </tr>
                  ) : (
                    paymentData.map((row) => (
                      <tr
                        key={row.paymentId}
                        className={`transition-colors duration-200 ${
                          isDark ? "bg-gray-800 hover:bg-gray-750" : "bg-white hover:bg-gray-50"
                        }`}
                      >
                        <td className={`px-3 py-3 text-center border-r ${isDark ? "border-gray-700" : "border-gray-200"}`}>
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(row.paymentId)}
                            onChange={() => toggleRow(row.paymentId)}
                            className="h-4 w-4 cursor-pointer"
                          />
                        </td>

                        <td className={`px-4 py-3 text-center border-r ${isDark ? "border-gray-700 text-white" : "border-gray-200 text-gray-900"}`}>
                          {row.slNo}
                        </td>
                        <td className={`px-4 py-3 text-center border-r ${isDark ? "border-gray-700 text-white" : "border-gray-200 text-gray-900"}`}>
                          {row.date}
                        </td>
                        <td className={`px-4 py-3 text-center border-r ${isDark ? "border-gray-700 text-blue-400" : "border-gray-200 text-blue-600"}`}>
                          {row.entryNumber}
                        </td>
                        <td className={`px-4 py-3 text-center border-r ${isDark ? "border-gray-700 text-white" : "border-gray-200 text-gray-900"}`}>
                          {row.supervisorName}
                        </td>
                        <td className={`px-4 py-3 text-center ${isDark ? "text-white" : "text-gray-900"}`}>
                          {row.branchName}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </PaymentLayoutWrapper>
  );
};

export default SupervisorPaymentListing;